import apiClient from './apiClient';
import {
  Program,
  ProgramStats,
  ProgramFormData,
  ProgramVersion,
} from '../types/program';

class ProgramService {
  /**
   * Fetch all programs
   */
  async getAllPrograms(): Promise<Program[]> {
    try {
      const response = await apiClient.get('/programs');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      throw error;
    }
  }

  /**
   * Fetch a single program by ID
   */
  async getProgramById(id: string): Promise<Program> {
    try {
      const response = await apiClient.get(`/programs/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to fetch program ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new program
   */
  async createProgram(data: ProgramFormData): Promise<Program> {
    try {
      const response = await apiClient.post('/programs', data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to create program:', error);
      throw error;
    }
  }

  /**
   * Update an existing program
   */
  async updateProgram(id: string, data: ProgramFormData): Promise<Program> {
    try {
      const response = await apiClient.patch(`/programs/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to update program ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a program
   */
  async deleteProgram(id: string): Promise<void> {
    try {
      await apiClient.delete(`/programs/${id}`);
    } catch (error) {
      console.error(`Failed to delete program ${id}:`, error);
      throw error;
    }
  }

  /**
   * Upload program cover image
   */
  async uploadCoverImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/programs/upload-cover', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.data.imageUrl;
    } catch (error) {
      console.error('Failed to upload cover image:', error);
      throw error;
    }
  }

  /**
   * Upload exercise video
   */
  async uploadExerciseVideo(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/programs/upload/video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.data.url;
    } catch (error) {
      console.error('Failed to upload exercise video:', error);
      throw error;
    }
  }

  /**
   * Fetch program statistics
   */
  async getProgramStats(): Promise<ProgramStats> {
    try {
      const response = await apiClient.get('/programs/stats');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch program stats:', error);
      throw error;
    }
  }

  /**
   * Create a new version of an existing program
   */
  async createProgramVersion(programId: string, versionData: ProgramVersion): Promise<Program> {
    try {
      const response = await apiClient.post(`/programs/${programId}/versions`, versionData);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to create program version for ${programId}:`, error);
      throw error;
    }
  }
}

export const programService = new ProgramService();
